import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getBillingDeploymentReport } from "../../services/Dashboard";
import { getColumnsData } from "./DeploymentsReportColumns";
import { useQuery } from "react-query";
import { isValidDate } from "../../services/Utils";
import SearchIcon from "@mui/icons-material/Search";
import getDataGridToolbarProps from "../common/CustomToolbar";

const DEPLOYMENT_REPORT_STATUS = [
  {
    label: "Installation Completed",
    value: "INSTALLATION_COMPLETED",
  },
  { label: "Download Completed", value: "DOWNLOAD_COMPLETED" },
];

const DeploymentsReport = (props) => {
  const CustomToolbar = getDataGridToolbarProps("Deployments Report", true);
  const columns = getColumnsData();
  const [status, setStatus] = useState(DEPLOYMENT_REPORT_STATUS[0].value);
  const [picker, setPicker] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0)
  );
  const [pickerInputErrorText, setPickerInputErrorText] = useState(null);
  const [month, setMonth] = useState(picker.getMonth() + 1);
  const [year, setYear] = useState(picker.getFullYear());
  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [fetchData, setFetchData] = useState(true);
  const { isLoading: logsLoading, isFetching } = useQuery(
    [status, month, year],
    getBillingDeploymentReport,
    {
      enabled: fetchData,
      onSuccess: ({ data }) => {
        const tblData = data.map((row, index) => {
          return { index, ...row };
        });
        setRows(tblData);
      },
      onSettled: () => {
        setFetchData(false);
      },
      refetchOnWindowFocus: false,
    }
  );

  const handleMonthChange = (val = null) => {
    if (!isValidDate(new Date(val))) {
      setPickerInputErrorText("Invalid Month!");
      return;
    }
    setPickerInputErrorText(null);
    setPicker(val);
    console.log(val);
  };

  const handleStatusChange = (event) => {
    const {
      target: { value: status },
    } = event;
    setStatus(status);
  };

  const processSearch = () => {
    if (!isValidDate(new Date(picker))) {
      return;
    }
    setMonth(picker.getMonth() + 1);
    setYear(picker.getFullYear());
    setFetchData(true);
  };

  const commonProps = { refresh, setRefresh };

  return (
    <div className="fragmentContainer">
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <Card sx={{ height: "96px" }}>
            <CardContent className="tile-cards">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FormControl variant="standard" sx={{ m: 1, minWidth: 220 }}>
                    <InputLabel>Deployment Status</InputLabel>
                    <Select
                      value={status}
                      onChange={handleStatusChange}
                      label="Deployment Status"
                    >
                      {DEPLOYMENT_REPORT_STATUS.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    <DatePicker
                      disableFuture
                      label="Month"
                      views={["month", "year"]}
                      value={picker}
                      timezone="UTC"
                      onChange={(val) => handleMonthChange(val)}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          id="standard-basic"
                          variant="standard"
                          error={!!pickerInputErrorText}
                          onKeyDown={(e) => e.preventDefault()}
                          helperText={pickerInputErrorText}
                        />
                      )}
                      maxDate={new Date()}
                      minDate={new Date("01-01-2023")}
                      required
                    />
                  </FormControl>
                  <Button
                    disabled={!!pickerInputErrorText}
                    sx={{ ml: "8px" }}
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => processSearch()}
                  >
                    Search
                  </Button>
                </div>
              </LocalizationProvider>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12} lg={12}>
          <Card>
            <CardContent
              sx={{
                padding: 0,
                ":last-child": {
                  padding: 0,
                },
              }}
            >
              <Box sx={{ height: "calc(100vh - 280px)", width: "100%" }}>
                <DataGrid
                  loading={logsLoading || isFetching}
                  getRowId={(row) => row?.index}
                  rows={rows || []}
                  columns={columns}
                  checkboxSelection
                  disableSelectionOnClick
                  experimentalFeatures={{ newEditingApi: true }}
                  components={{ Toolbar: CustomToolbar }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default DeploymentsReport;
